// The supplier bid screen (Lane 1331, Phase 2 slice 2B; new look Lane 1394,
// mockup v2).
//
// What it does, in order of the data:
// - Loads the supplier's own draft, submitted values and history (GET /draft).
// - Shows the parts in a spreadsheet grid. Each input shows the draft value
//   when the part has a draft row, else the value submitted in THIS round
//   (offer or no bid), else nothing. An offer carried from an earlier round
//   never puts its PRICE in the inputs (slice 2D): the read-only "Round N-1 price"
//   column shows the supplier's own price as it stood when this round opened (the
//   server's `previous_round`, Lane 1394), and an empty price means "keep the
//   current offer". Its minimum order, lead time and note DO start in the inputs
//   (Lane 1440: typing only a new price used to submit an offer without them); a
//   row left exactly so is never saved. No estimated value or buyer's current price is ever shown,
//   and nothing about other suppliers.
// - Saves edits into the draft in batches (1.5 s after the last edit, when
//   focus leaves the grid, and before submit, download or upload), at most 500
//   rows per call, with the draft revision the server gave last. The server
//   refuses a stale revision, so two windows cannot overwrite each other.
// - Submits the saved draft after a confirm, with one Idempotency-Key per
//   submit action (kept for a retry of that same action).
// - Excel: an upload is checked first (the server reads the file with
//   `preview` and saves nothing); the supplier sees each change and presses
//   Apply, which sends the same file on the save path (decision D1).
//
// The save state lives in refs (not React state) so the delayed save always
// reads the latest edits and revision; `rerender` redraws after a change.
//
// The screen stays mounted while the event page shows another tab (`tab`), so
// unsaved edits are never lost; its Submit, Withdraw and Decline buttons are
// drawn in the event header (`actionsEl`, a React portal), and what they
// report is drawn above the tabs (`resultsEl`), so it shows on every tab.
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pager } from '../../components/ui/DataPrimitives';
import { portalApi, newActionKey } from './portalApi';
import { formatDueAt, formatUnitPrice, timeLeftText } from './portalView';
import BidGrid from './BidGrid';
import {
  Banner, Modal, Receipt, submitReceipt, HistoryPanel, UploadResult, UploadReview, WithdrawPanel,
  DeclinePanel,
} from './BidPanels';
import {
  EMPTY_ROW, EVENT_NOTE_MAX, NOTE_MAX, SAVE_BATCH, SUBMITTED_STATES, SERVER_ROW_REASONS,
  rowFromServer, rowToPayload, draftEntryFromPayload, validateRow, sameRow, answerOf,
  rowNeedsAnswer, trimPrice, shortReceipt,
} from './bidModel';

const PAGE_SIZE = 250;
const SAVE_DELAY_MS = 1500;
const FILTERS = [['all', 'All'], ['none', 'Not quoted yet'], ['priced', 'Priced'], ['no_bid', 'No bid']];
// Refusals that mean the round or the event moved on: reload everything.
const EVENT_MOVED = ['scope_changed', 'round_closed', 'not_in_round'];
const STALE_TEXT = 'Your draft changed in another window. We reloaded it.';

// An offer that counts now but was submitted in an earlier round.
function isCarried(c, draft) {
  return Boolean(c && draft && draft.round && c.round && c.round < draft.round);
}

// The details of an offer carried from an earlier round (Lane 1440): its minimum
// order, lead time and note, never its price (an empty price keeps the offer).
function carriedRow(pn, draft) {
  const c = draft && draft.current[pn];
  if (!c || c.state !== 'offer' || !isCarried(c, draft)) return null;
  return { ...rowFromServer(c), price: '', no_bid: false };
}

// A row that is only a carried offer's details: nothing to save, nothing to answer.
function isCarriedOnly(pn, row, draft) {
  const carried = carriedRow(pn, draft);
  return Boolean(carried) && sameRow(row, carried);
}

// The row a part shows: unsaved edit, else draft row, else the value submitted
// in this round, else a carried offer's details (slice 2D; Lane 1440).
function savedRow(pn, draft) {
  if (!draft) return EMPTY_ROW;
  if (draft.draft[pn]) return rowFromServer(draft.draft[pn]);
  const c = draft.current[pn];
  if (c && SUBMITTED_STATES.includes(c.state) && !isCarried(c, draft)) return rowFromServer(c);
  return carriedRow(pn, draft) || EMPTY_ROW;
}
function shownRow(pn, draft, edits) {
  return edits[pn] || savedRow(pn, draft);
}

// The answer a part has now: what is on screen, else the current offer it
// keeps (an empty row keeps the carried offer).
function answerFor(pn, draft, edits) {
  const shown = answerOf(shownRow(pn, draft, edits));
  if (shown !== 'none' || !draft) return shown;
  const c = draft.current[pn];
  if (c && c.state === 'offer') return 'priced';
  if (c && c.state === 'no_bid') return 'no_bid';
  return 'none';
}

// Why a previous-round price no longer counts (it shows struck through).
const GONE_TEXT = {
  needs_resubmission: 'The buyer changed this part or the terms. Quote it again.',
  needs_acknowledgement: 'Acknowledge the buyer\'s change to keep this price.',
  withdrawn: 'You withdrew this price.',
  expired: 'This price expired.',
  not_in_scope: 'This part is no longer in the event.',
};
const GONE_DEFAULT = 'This price no longer counts.';

// The read-only previous-round cell: the supplier's own price as it stood
// when this round opened, struck through when it no longer counts and no
// price from this round replaced it.
function previousCell(pn, draft, currency) {
  const prev = draft.previous_round && draft.previous_round.prices[pn];
  if (!prev) return null;
  const text = prev.no_bid ? 'No bid' : formatUnitPrice(prev.price, currency);
  const c = draft.current[pn];
  const counts = c && SUBMITTED_STATES.includes(c.state);
  return { text, gone: counts ? '' : (GONE_TEXT[c && c.state] || GONE_DEFAULT) };
}

// The parts a published change asks the supplier to act on (slice 2E).
function partsToActOn(current) {
  const quote = [];
  const ack = [];
  Object.entries(current || {}).forEach(([pn, c]) => {
    if (c.state === 'needs_resubmission') quote.push(pn);
    if (c.state === 'needs_acknowledgement') ack.push(pn);
  });
  return { quote: quote.sort(), ack: ack.sort() };
}

function listText(items, max = 6) {
  const shown = items.slice(0, max).join(', ');
  return items.length > max ? `${shown} and ${(items.length - max).toLocaleString()} more` : shown;
}

// Draft rows the buyer's change removed, so the supplier enters them again.
function RebaseNotices({ notices, onDismiss }) {
  if (!notices.length) return null;
  return (
    <Banner tone="info" role="status"
      actions={<button type="button" className="srcp-btn srcp-btn--sm" onClick={onDismiss}>I have seen this</button>}>
      <div>The buyer changed the event, so we removed some prices from your draft. Enter them again:</div>
      <ul>
        {notices.map((n, i) => (
          <li key={`${n.amendment_number}-${i}`}>
            {n.earlier ? `Earlier changes: ${n.count.toLocaleString()} parts.`
              : `Change ${n.amendment_number}: ${n.parts.join(', ')}${n.count > n.parts.length
                ? ` and ${(n.count - n.parts.length).toLocaleString()} more` : ''}.`}
          </li>
        ))}
      </ul>
    </Banner>
  );
}

function fileNameFor(event) {
  const slug = String(event.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '').slice(0, 60);
  return slug ? `quote-${slug}.xlsx` : 'quote.xlsx';
}

// Every page of the parts list (for the Priced / No bid / Not quoted filters,
// which the server does not filter). 250 per page, four requests at a time.
async function loadAllParts(eventId, search) {
  const first = await portalApi.parts(eventId, { page: 1, search });
  const items = [...first.items];
  const pages = first.total_pages || 1;
  for (let start = 2; start <= pages; start += 4) {
    const batch = [];
    for (let p = start; p < start + 4 && p <= pages; p += 1) batch.push(portalApi.parts(eventId, { page: p, search }));
    const results = await Promise.all(batch);
    results.forEach((r) => items.push(...r.items));
  }
  return { items, total: first.total };
}

const numOrNull = (v) => (v === null || v === undefined || String(v).trim() === '' ? null : Number(v));

function Tile({ k, v }) {
  return (
    <div className="srcp-tile">
      <span className="srcp-tile-k">{k}</span>
      <span className="srcp-tile-v">{v}</span>
    </div>
  );
}

export default function BidScreen({
  event, eventId, onEventChanged, refreshKey = 0, tab = 'quote', actionsEl = null,
  resultsEl = null, pendingChange = null, onDraft,
}) {
  const [, rerender] = useReducer((x) => x + 1, 0);
  const draftRef = useRef(null);
  const editsRef = useRef({});
  const dirtyRef = useRef(new Set());
  const serverErrRef = useRef({});
  const noteRef = useRef('');
  const noteDirtyRef = useRef(false);
  const timerRef = useRef(null);
  const savingRef = useRef(null);
  const submitKeyRef = useRef(null);
  const fileRef = useRef(null);
  const onDraftRef = useRef(onDraft);
  onDraftRef.current = onDraft;

  const [draftError, setDraftError] = useState('');
  const [draftGen, setDraftGen] = useState(0);
  const [save, setSave] = useState({ state: 'idle', message: '' });
  const [notice, setNotice] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [panel, setPanel] = useState(null);
  const [upload, setUpload] = useState(null);
  const [review, setReview] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [excelOpen, setExcelOpen] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [eventNoteOpen, setEventNoteOpen] = useState(false);

  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [partsGen, setPartsGen] = useState(0);
  const [serverPage, setServerPage] = useState(null);
  const [allParts, setAllParts] = useState(null);
  const [partsError, setPartsError] = useState('');
  const [eventTotal, setEventTotal] = useState(null);
  const partsRequest = useRef(0);
  const excelRef = useRef(null);

  // ---- loading ------------------------------------------------------------------

  const loadDraft = useCallback(async ({ discard = false } = {}) => {
    try {
      const d = await portalApi.draft(eventId);
      if (discard) {
        editsRef.current = {};
        dirtyRef.current = new Set();
        serverErrRef.current = {};
        noteDirtyRef.current = false;
      }
      draftRef.current = d;
      if (!noteDirtyRef.current) noteRef.current = d.event_note || '';
      setDraftError('');
      setDraftGen((g) => g + 1);
      rerender();
      if (onDraftRef.current) onDraftRef.current(d);
      return d;
    } catch (e) {
      if (e.status !== 401) setDraftError(e.message);
      return null;
    }
  }, [eventId]);

  useEffect(() => { loadDraft(); }, [loadDraft]);
  // An acknowledgement elsewhere on the page changes which prices count
  // (slice 2E): read the draft view again, keeping unsaved edits.
  useEffect(() => { if (refreshKey) loadDraft(); }, [refreshKey, loadDraft]);

  // The list of draft rows a published change removed (slice 2E): read, then
  // dismissed with an empty draft save that carries the dismiss flag.
  const dismissRebaseNotices = async () => {
    const d = draftRef.current;
    if (!d) return;
    try {
      await portalApi.saveDraft(eventId, { scope_version: d.scope_version, draft_revision: d.draft_revision,
        parts: [], dismiss_rebase_notices: true });
    } catch (e) {
      if (e.status !== 401) setNotice(e.message);
    }
    await loadDraft();
  };

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchText.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  // "All" pages on the server; the other filters load every page once and page
  // on screen, so a page change in those views does not refetch.
  const listMode = filter === 'all' ? 'server' : 'screen';
  const serverPageNo = listMode === 'server' ? page : 0;
  useEffect(() => {
    const id = partsRequest.current + 1;
    partsRequest.current = id;
    setPartsError('');
    const fail = (e) => { if (partsRequest.current === id && e.status !== 401) setPartsError(e.message); };
    if (listMode === 'server') {
      portalApi.parts(eventId, { page: serverPageNo, search })
        .then((r) => {
          if (partsRequest.current !== id) return;
          setServerPage(r);
          if (!search) setEventTotal(r.total);
        })
        .catch(fail);
    } else {
      setAllParts(null);
      loadAllParts(eventId, search)
        .then((r) => {
          if (partsRequest.current !== id) return;
          setAllParts(r);
          if (!search) setEventTotal(r.total);
        })
        .catch(fail);
    }
  }, [eventId, listMode, serverPageNo, search, partsGen]);

  // The Excel menu closes on a click outside it.
  useEffect(() => {
    if (!excelOpen) return undefined;
    const close = (e) => { if (excelRef.current && !excelRef.current.contains(e.target)) setExcelOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [excelOpen]);

  // ---- editing rules -----------------------------------------------------------

  const dueMs = event.due_at ? new Date(event.due_at).getTime() : NaN;
  const pastDue = Number.isFinite(dueMs) && dueMs <= Date.now();
  const draft = draftRef.current;
  const canEdit = Boolean(event.status === 'open' && event.in_current_round && draft && draft.round && !pastDue);
  const gridEditable = canEdit && !confirming && !submitting;

  // Redraw at the due time so the grid locks without a reload.
  useEffect(() => {
    if (!Number.isFinite(dueMs) || pastDue) return undefined;
    const wait = dueMs - Date.now();
    if (wait > 2147483647) return undefined;
    const t = setTimeout(rerender, wait + 500);
    return () => clearTimeout(t);
  }, [dueMs, pastDue]);

  // The "Time left" tile counts down once a minute.
  useEffect(() => {
    if (pastDue) return undefined;
    const t = setInterval(rerender, 60000);
    return () => clearInterval(t);
  }, [pastDue]);

  // ---- saving ----------------------------------------------------------------------

  const isInvalid = (pn) => Boolean(serverErrRef.current[pn])
    || (dirtyRef.current.has(pn) && Object.keys(validateRow(editsRef.current[pn])).length > 0);

  const flush = useCallback(async () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    while (savingRef.current) {
      await savingRef.current.catch(() => {});
    }
    const sendable = () => [...dirtyRef.current].filter((pn) => !serverErrRef.current[pn]
      && Object.keys(validateRow(editsRef.current[pn])).length === 0);
    if (!draftRef.current || (sendable().length === 0 && !noteDirtyRef.current)) {
      return [...dirtyRef.current].length === 0 && Object.keys(serverErrRef.current).length === 0;
    }

    const run = (async () => {
      setSave({ state: 'saving', message: '' });
      for (;;) {
        const batch = sendable().slice(0, SAVE_BATCH);
        const noteSent = noteDirtyRef.current ? noteRef.current : null;
        if (batch.length === 0 && noteSent === null) break;
        const sent = {};
        batch.forEach((pn) => { sent[pn] = editsRef.current[pn]; });
        // A row back to a carried offer's details removes its draft row (Lane 1440).
        const payloads = batch.map((pn) => rowToPayload(pn,
          isCarriedOnly(pn, sent[pn], draftRef.current) ? EMPTY_ROW : sent[pn]));
        const body = {
          scope_version: draftRef.current.scope_version,
          draft_revision: draftRef.current.draft_revision,
          parts: payloads,
        };
        if (noteSent !== null) {
          if (noteSent.trim()) body.event_note = noteSent;
          else body.clear_event_note = true;
        }
        let res;
        try {
          res = await portalApi.saveDraft(eventId, body);
        } catch (e) {
          if (e.status === 401) return false;
          if (e.code === 'stale_draft') {
            await loadDraft({ discard: true });
            setNotice(STALE_TEXT);
            setSave({ state: 'idle', message: '' });
            return false;
          }
          if (EVENT_MOVED.includes(e.code)) {
            await loadDraft({ discard: true });
            setNotice(`${e.message} We reloaded the page.`);
            setSave({ state: 'idle', message: '' });
            setPartsGen((g) => g + 1);
            onEventChanged();
            return false;
          }
          let marked = 0;
          if (e.code === 'invalid_parts') {
            e.errors.forEach((er) => {
              const pn = batch[er.index] || er.part_number;
              if (pn) { serverErrRef.current[pn] = SERVER_ROW_REASONS[er.reason] || 'This row was not saved.'; marked += 1; }
            });
          } else if (e.code === 'invalid_input') {
            e.errors.forEach((er) => {
              const loc = er.loc || [];
              const pn = loc[0] === 'parts' ? batch[loc[1]] : undefined;
              if (pn) { serverErrRef.current[pn] = 'This row was not saved. Check its values.'; marked += 1; }
            });
          }
          if (marked > 0) { rerender(); continue; } // the other rows go in the next call
          setSave({ state: 'error', message: e.message });
          return false;
        }
        const next = { ...draftRef.current, draft_revision: res.draft_revision, draft: { ...draftRef.current.draft } };
        payloads.forEach((p) => {
          const entry = draftEntryFromPayload(p);
          if (entry) next.draft[p.part_number] = entry;
          else delete next.draft[p.part_number];
        });
        if (noteSent !== null) next.event_note = noteSent.trim() || null;
        draftRef.current = next;
        batch.forEach((pn) => {
          // A row edited again while this call ran stays waiting for the next one.
          if (editsRef.current[pn] === sent[pn]) {
            delete editsRef.current[pn];
            dirtyRef.current.delete(pn);
          }
        });
        if (noteSent !== null && noteRef.current === noteSent) noteDirtyRef.current = false;
        rerender();
      }
      setSave({ state: 'saved', message: '' });
      return [...dirtyRef.current].length === 0 && Object.keys(serverErrRef.current).length === 0;
    })();
    savingRef.current = run;
    try {
      return await run;
    } finally {
      savingRef.current = null;
      rerender();
    }
  }, [eventId, loadDraft, onEventChanged]);

  const flushRef = useRef(flush);
  flushRef.current = flush;
  const scheduleSave = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { flushRef.current(); }, SAVE_DELAY_MS);
  };

  // Save what is waiting when the screen closes; warn before leaving the page.
  useEffect(() => {
    const warn = (e) => {
      if (dirtyRef.current.size || noteDirtyRef.current || savingRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      flushRef.current();
    };
  }, []);

  // An empty row (every cell cleared) removes the part's draft row on save:
  // emptying the cells is how a row is cleared.
  const onChange = (pn, field, value) => {
    if (!gridEditable) return;
    const next = { ...shownRow(pn, draftRef.current, editsRef.current), [field]: value };
    if (field === 'no_bid' && value) next.price = '';
    delete serverErrRef.current[pn];
    setNotice('');
    // Typed back to what is saved: nothing to send (only when no save is running,
    // since a running save may be about to change what is saved).
    if (!savingRef.current && sameRow(next, savedRow(pn, draftRef.current))) {
      delete editsRef.current[pn];
      dirtyRef.current.delete(pn);
    } else {
      editsRef.current[pn] = next;
      dirtyRef.current.add(pn);
      scheduleSave();
    }
    rerender();
  };

  const onNote = (value) => {
    if (!gridEditable) return;
    // The box stays open while typing, even when the text is emptied.
    setEventNoteOpen(true);
    noteRef.current = value;
    noteDirtyRef.current = true;
    scheduleSave();
    rerender();
  };

  // ---- counts --------------------------------------------------------------------

  const draftMap = (draft && draft.draft) || {};
  const currentMap = (draft && draft.current) || {};
  const edits = editsRef.current;
  const draftRows = Object.values(draftMap);
  const draftPriced = draftRows.filter((d) => !d.no_bid && d.price !== null && d.price !== undefined).length;
  const draftNoBid = draftRows.filter((d) => d.no_bid).length;
  const draftUnanswered = draftRows.length - draftPriced - draftNoBid;
  const submittedParts = Object.entries(currentMap)
    .filter(([, c]) => SUBMITTED_STATES.includes(c.state))
    .map(([pn, c]) => ({ part_number: pn, text: c.state === 'no_bid' ? 'No bid' : `Price ${trimPrice(c.price)}` }))
    .sort((a, b) => a.part_number.localeCompare(b.part_number));
  const invalidParts = [...new Set([...dirtyRef.current].filter(isInvalid)
    .concat(Object.keys(serverErrRef.current)))].sort();
  const invalidCount = invalidParts.length;

  // Event-wide answers as shown on screen (saved plus waiting edits).
  const answerCounts = { priced: 0, no_bid: 0 };
  let readyToSend = 0;
  let needsAnswer = 0;
  new Set([...Object.keys(draftMap), ...Object.keys(currentMap), ...Object.keys(edits)]).forEach((pn) => {
    const a = answerFor(pn, draft, edits);
    if (a !== 'none') answerCounts[a] += 1;
    const row = shownRow(pn, draft, edits);
    if (answerOf(row) !== 'none' && (draftMap[pn] || dirtyRef.current.has(pn))) readyToSend += 1;
    if ((draftMap[pn] || edits[pn]) && rowNeedsAnswer(row) && !isCarriedOnly(pn, row, draft)) needsAnswer += 1;
  });
  const filterCount = {
    all: eventTotal,
    priced: answerCounts.priced,
    no_bid: answerCounts.no_bid,
    none: eventTotal === null ? null : Math.max(0, eventTotal - answerCounts.priced - answerCounts.no_bid),
  };

  // The filtered list is fixed when the filter, the list or the saved draft
  // reloads, not on every keystroke, so a row does not vanish while you type.
  const filtered = useMemo(() => {
    if (filter === 'all' || !allParts) return null;
    return allParts.items.filter(
      (p) => answerFor(p.part_number, draftRef.current, editsRef.current) === filter);
  }, [filter, allParts, draftGen]); // eslint-disable-line react-hooks/exhaustive-deps

  let view = null;
  if (filter === 'all' && serverPage) view = serverPage;
  if (filtered) {
    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const at = Math.min(page, pages);
    view = {
      items: filtered.slice((at - 1) * PAGE_SIZE, at * PAGE_SIZE),
      total: filtered.length, page: at, page_size: PAGE_SIZE, total_pages: pages,
    };
  }

  // ---- actions ------------------------------------------------------------------

  const saving = save.state === 'saving' || Boolean(savingRef.current);
  const canSubmit = canEdit && !saving && !submitting && invalidCount === 0 && readyToSend > 0;

  const startSubmit = async () => {
    setSubmitError('');
    setReceipt(null);
    const ok = await flush();
    if (!ok || !draftRef.current) return;
    const rows = Object.values(draftRef.current.draft);
    if (!rows.some((d) => d.no_bid || (d.price !== null && d.price !== undefined))) return;
    setConfirming(true);
  };

  const confirmSubmit = async () => {
    const rev = draftRef.current.draft_revision;
    if (!submitKeyRef.current || submitKeyRef.current.rev !== rev) submitKeyRef.current = { rev, key: newActionKey() };
    setSubmitting(true);
    setSubmitError('');
    try {
      const r = await portalApi.submit(eventId, rev, submitKeyRef.current.key);
      submitKeyRef.current = null;
      setConfirming(false);
      setReceipt(submitReceipt(r));
      await loadDraft({ discard: true });
      onEventChanged();
    } catch (e) {
      if (e.status === 401) return;
      if (e.status === 0 || e.status >= 500) {
        setSubmitError(`${e.message} It is safe to press Submit again.`);
        return;
      }
      submitKeyRef.current = null;
      setConfirming(false);
      if (e.code === 'stale_draft') {
        await loadDraft({ discard: true });
        setNotice(`${STALE_TEXT} Check it and submit again.`);
      } else if (EVENT_MOVED.includes(e.code)) {
        await loadDraft({ discard: true });
        setPartsGen((g) => g + 1);
        onEventChanged();
        setSubmitError(e.message);
      } else {
        setSubmitError(e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const download = async () => {
    setExcelOpen(false);
    setDownloadError('');
    setDownloading(true);
    await flush();
    try {
      const blob = await portalApi.bidFile(eventId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileNameFor(event);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (e.status !== 401) setDownloadError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  // Step 1 of an upload: the server reads the file and saves nothing. A file
  // it refuses (another event, an earlier round, a part twice, too large, a
  // stale draft) shows why, and the draft is untouched.
  const onFile = async (file) => {
    if (!file) return;
    setUpload({ busy: true });
    setReview(null);
    try {
      await flush();
      if (!draftRef.current) { setUpload({ error: 'Your draft is not loaded. Reload the page.' }); return; }
      const r = await portalApi.uploadBidFile(eventId, file, draftRef.current.draft_revision, { preview: true });
      setUpload(null);
      setReview({ file, fileName: file.name, parts: r.parts || [], errors: r.errors || [] });
    } catch (e) {
      if (e.status === 401) { setUpload(null); return; }
      setUpload(null);
      if (e.code === 'stale_draft') {
        await loadDraft({ discard: true });
        setReview({ file, fileName: file.name, refused: `${STALE_TEXT} Upload the file again.` });
      } else if (EVENT_MOVED.includes(e.code)) {
        await loadDraft({ discard: true });
        setPartsGen((g) => g + 1);
        onEventChanged();
        setReview({ file, fileName: file.name, refused: e.message });
      } else {
        setReview({ file, fileName: file.name, refused: e.message,
          rows: (e.errors || []).filter((r) => r.row !== undefined) });
      }
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Step 2, Apply: the same file on the save path, unchanged.
  const applyFile = async () => {
    if (!review || !review.file) return;
    const { file } = review;
    setUpload({ busy: true });
    try {
      await flush();
      if (!draftRef.current) { setUpload({ error: 'Your draft is not loaded. Reload the page.' }); return; }
      const r = await portalApi.uploadBidFile(eventId, file, draftRef.current.draft_revision);
      setReview(null);
      setUpload({ result: r, errors: r.errors || [] });
      await loadDraft();
    } catch (e) {
      if (e.status === 401) { setUpload(null); return; }
      setReview(null);
      if (e.code === 'stale_draft') {
        await loadDraft({ discard: true });
        setUpload({ error: `${STALE_TEXT} Upload the file again.` });
      } else if (EVENT_MOVED.includes(e.code)) {
        await loadDraft({ discard: true });
        setPartsGen((g) => g + 1);
        onEventChanged();
        setUpload({ error: e.message });
      } else {
        setUpload({ error: e.message, errors: (e.errors || []).filter((r) => r.row !== undefined) });
      }
    }
  };

  const cancelReview = () => {
    setReview(null);
    setNotice('Nothing changed in your draft.');
  };

  const afterWithdraw = async (r) => {
    setPanel(null);
    const n = r.parts ? r.parts.length : null;
    setReceipt({
      title: n === null ? 'Every price you submitted is withdrawn.' : `Withdrew ${n.toLocaleString()} ${n === 1 ? 'part' : 'parts'}.`,
      lines: [`Receipt ${shortReceipt(r.receipt)}`],
    });
    await loadDraft();
    onEventChanged();
  };

  const afterDecline = async (r) => {
    setPanel(null);
    setReceipt({
      title: 'You declined this event.',
      lines: ['Every price you submitted is withdrawn and your draft is cleared.', `Receipt ${shortReceipt(r.receipt)}`],
    });
    await loadDraft({ discard: true });
    onEventChanged();
  };

  // ---- drawing -------------------------------------------------------------------

  if (draftError) return tab === 'quote' || tab === 'history' ? <div className="srcp-error" role="alert">{draftError}</div> : null;
  if (!draft) return tab === 'quote' || tab === 'history' ? <div className="srcp-loading">Loading your quote…</div> : null;

  const busyAction = saving || submitting || Boolean(upload && upload.busy) || downloading;
  const canWithdraw = event.status === 'open' && submittedParts.length > 0;
  const canDecline = canEdit && event.own_status !== 'declined';
  const currency = event.terms && event.terms.currency;
  const waitingRows = dirtyRef.current.size;
  const round = draft.round || event.round || 1;
  const showPrevious = Boolean(draft.previous_round && round >= 2);
  const total = eventTotal;
  const todo = total === null ? null : Math.max(0, total - answerCounts.priced - answerCounts.no_bid);
  const pct = (n) => (total ? `${((n / total) * 100).toFixed(1)}%` : '0%');
  const act = partsToActOn(currentMap);
  // Parts whose current price the buyer changed (Lane 1395 QA): told plainly, never who
  // or why.
  const changedByBuyer = Object.entries(currentMap || {})
    .filter(([, c]) => c.source === 'buyer_edit' && c.state === 'offer').map(([pn]) => pn).sort();

  let saveText = '';
  if (save.state === 'saving') saveText = 'Saving…';
  else if (save.state === 'error') saveText = `Not saved. ${save.message}`;
  else if (waitingRows || noteDirtyRef.current) saveText = invalidCount ? '' : 'Changes waiting to save';
  else if (save.state === 'saved') saveText = 'Saved';

  // The upload check compares the file with the part's DRAFT row (unsaved
  // edits, else the saved draft row), never with a submitted offer; the offer
  // that counts now only names a row that repeats it (Codex finding 1).
  const nowOf = (pn) => {
    const row = edits[pn] || (draftMap[pn] ? rowFromServer(draftMap[pn]) : EMPTY_ROW);
    const c = currentMap[pn];
    const submitted = c && SUBMITTED_STATES.includes(c.state) ? {
      price: c.state === 'no_bid' ? null : numOrNull(c.price),
      no_bid: c.state === 'no_bid',
      text: isCarried(c, draft) ? `Same as your round ${round - 1} price` : 'Same as your submitted price',
    } : null;
    return {
      draft: {
        price: row.no_bid ? null : numOrNull(row.price), no_bid: row.no_bid,
        lead: numOrNull(row.lead), moq: numOrNull(row.moq), note: row.note.trim(),
      },
      submitted,
    };
  };

  const headerActions = actionsEl && createPortal(
    <>
      {canWithdraw && (
        <button type="button" className="srcp-btn" disabled={confirming || panel === 'withdraw'}
          onClick={() => setPanel('withdraw')}>Withdraw prices</button>
      )}
      {canDecline && (
        <button type="button" className="srcp-btn" disabled={confirming || panel === 'decline'}
          onClick={() => setPanel('decline')}>Decline event</button>
      )}
      {canEdit && (
        <button type="button" className="srcp-btn srcp-btn--primary"
          disabled={!canSubmit || confirming || Boolean(review)} onClick={startSubmit}>Submit quote</button>
      )}
    </>,
    actionsEl,
  );

  const noteRow = noteFor ? shownRow(noteFor, draft, edits) : null;
  const windows = (
    <>
      {confirming && (
        <Modal title="Submit your quote" busy={submitting}
          onClose={() => { setConfirming(false); setSubmitError(''); }}
          footer={(
            <>
              <button type="button" className="srcp-btn srcp-btn--primary" disabled={submitting} onClick={confirmSubmit}>
                {submitting ? 'Submitting…' : 'Yes, submit'}
              </button>
              <button type="button" className="srcp-btn" disabled={submitting}
                onClick={() => { setConfirming(false); setSubmitError(''); }}>Back</button>
            </>
          )}>
          <div className="srcp-confirm">
            <div className="srcp-strong">
              Submit {draftPriced.toLocaleString()} priced and {draftNoBid.toLocaleString()} no-bid parts to {event.buyer_name}?
            </div>
            <div>Parts you did not change keep your earlier submitted price.</div>
            {draftUnanswered > 0 && (
              <div>
                {draftUnanswered === 1 ? 'One part has' : `${draftUnanswered.toLocaleString()} parts have`} details but no price
                and no No bid. {draftUnanswered === 1 ? 'It is' : 'They are'} not sent and {draftUnanswered === 1 ? 'is' : 'are'} cleared from your draft.
              </div>
            )}
            {(draft.event_note || '').trim() && <div>Your note to the buyer goes with this submission.</div>}
            {event.due_at && <div>You can change your prices and submit again until {formatDueAt(event.due_at)}.</div>}
          </div>
          {submitError && <div className="srcp-error" role="alert">{submitError}</div>}
        </Modal>
      )}
      {panel === 'withdraw' && canWithdraw && (
        <WithdrawPanel eventId={eventId} submitted={submittedParts} onDone={afterWithdraw} onCancel={() => setPanel(null)} />
      )}
      {panel === 'decline' && canDecline && (
        <DeclinePanel eventId={eventId} submittedCount={submittedParts.length} onDone={afterDecline}
          onCancel={() => setPanel(null)} />
      )}
      {noteFor && noteRow && (
        <Modal title={`Your note for ${noteFor}`} onClose={() => setNoteFor(null)}
          footer={<button type="button" className="srcp-btn srcp-btn--primary" onClick={() => setNoteFor(null)}>Done</button>}>
          <div className="srcp-field">
            <label className="srcp-label" htmlFor="srcp-part-note">The buyer reads this note with your price for this part.</label>
            <textarea id="srcp-part-note" className="srcp-input srcp-textarea" rows={4} maxLength={NOTE_MAX}
              value={noteRow.note} disabled={!gridEditable} autoFocus
              onChange={(e) => onChange(noteFor, 'note', e.target.value)} />
            <span className="srcp-hint">
              {gridEditable ? 'Saved in your draft as you type. Sent with your next submission.' : 'You cannot change it now.'}
            </span>
          </div>
        </Modal>
      )}
    </>
  );

  // What the header actions and the Excel menu report (receipts, refusals,
  // stale-draft notices, upload results): drawn above the tabs, so a result is
  // seen whichever tab the button was pressed on (Codex slice B finding 2).
  const resultsBody = (
    <>
      {notice && <Banner role="status">{notice}</Banner>}
      <Receipt receipt={receipt} onClose={() => setReceipt(null)} />
      {!confirming && submitError && <div className="srcp-error" role="alert">{submitError}</div>}
      {downloadError && <div className="srcp-error" role="alert">{downloadError}</div>}
      {/* A failed save is shown here too, with its retry, so Submit on another tab
          never stops silently (Codex slice B round 2). */}
      {tab !== 'quote' && save.state === 'error' && (
        <div className="srcp-error" role="alert">{saveText}{' '}
          <button type="button" className="srcp-btn srcp-btn--sm" onClick={() => flush()}>Try again</button></div>
      )}
      <UploadResult upload={upload} onClose={() => setUpload(null)} />
    </>
  );
  const results = resultsEl ? createPortal(resultsBody, resultsEl) : resultsBody;

  if (tab === 'history') {
    return <>{headerActions}{windows}{results}<HistoryPanel history={draft.history} /></>;
  }
  if (tab !== 'quote') return <>{headerActions}{windows}{results}</>;

  const pending = pendingChange;
  return (
    <>
      {headerActions}
      {windows}
      {results}
      {event.status === 'closed' && <Banner>This round is closed for quotes. Your prices are shown below.</Banner>}
      {event.status === 'open' && pastDue && <Banner>The due time has passed. Quotes are closed. The buyer now compares offers.</Banner>}
      {event.status === 'open' && event.own_status === 'declined' && (
        <Banner>{canEdit ? 'You declined this event. You can still send a quote while the round is open.' : 'You declined this event.'}</Banner>
      )}
      {(pending || act.quote.length > 0 || act.ack.length > 0) && (
        <Banner tone={pending ? 'warn' : ''} actions={pending ? (
          <>
            <button type="button" className="srcp-btn" onClick={pending.onReview}>Review the changes</button>
            {pending.canAcknowledge && (
              <button type="button" className="srcp-btn srcp-btn--primary" disabled={pending.busy}
                onClick={pending.onAcknowledge}>Acknowledge</button>
            )}
          </>
        ) : null}>
          {pending && <div className="srcp-strong">The buyer changed this event. Review the changes.</div>}
          {act.quote.length > 0 && (
            <div>{canEdit ? `Quote again: ${listText(act.quote)}.` : `These prices no longer count: ${listText(act.quote)}. You cannot quote again now.`}</div>
          )}
          {act.ack.length > 0 && (
            <div>Acknowledge the change to keep your price on {act.ack.length === 1 ? 'one part' : `${act.ack.length.toLocaleString()} parts`}: {listText(act.ack)}.</div>
          )}
        </Banner>
      )}
      {changedByBuyer.length > 0 && (
        <Banner>The buyer changed your price on {changedByBuyer.length === 1 ? 'one part' : `${changedByBuyer.length.toLocaleString()} parts`}: {listText(changedByBuyer)}. The new prices are your current offer.</Banner>
      )}
      <RebaseNotices notices={draft.rebase_notices || []} onDismiss={dismissRebaseNotices} />

      {review && (
        <UploadReview review={review} nowOf={nowOf} currency={currency}
          applying={Boolean(upload && upload.busy)} onApply={applyFile} onCancel={cancelReview}
          onDownload={() => { setReview(null); download(); }} />
      )}

      {!review && (
        <>
          <section className="srcp-card" aria-label="Your progress">
            <div className="srcp-tiles">
              <Tile k="Time left" v={timeLeftText(event.due_at)} />
              <Tile k="Parts priced" v={total === null ? '' : `${answerCounts.priced.toLocaleString()} of ${total.toLocaleString()}`} />
              <Tile k="No bid" v={answerCounts.no_bid.toLocaleString()} />
              <Tile k="Still to do" v={todo === null ? '' : todo.toLocaleString()} />
            </div>
            <div className="srcp-prog">
              <div className="srcp-meter" aria-hidden="true">
                <span className="srcp-meter-priced" style={{ width: pct(answerCounts.priced) }} />
                <span className="srcp-meter-nobid" style={{ width: pct(answerCounts.no_bid) }} />
              </div>
              <span className="srcp-key"><span className="srcp-dot srcp-meter-priced" />Priced {answerCounts.priced.toLocaleString()}</span>
              <span className="srcp-key"><span className="srcp-dot srcp-meter-nobid" />No bid {answerCounts.no_bid.toLocaleString()}</span>
              <span className="srcp-key"><span className="srcp-dot srcp-dot--todo" />To do {todo === null ? '' : todo.toLocaleString()}</span>
            </div>
          </section>

          <section className="srcp-card" aria-label="Your quote">
            <div className="srcp-toolbar">
              <div className="srcp-chips" role="group" aria-label="Show parts">
                {FILTERS.map(([key, label]) => (
                  <button key={key} type="button" aria-pressed={filter === key}
                    className={`srcp-chip${filter === key ? ' srcp-chip--on' : ''}`}
                    onClick={() => { setFilter(key); setPage(1); }}>
                    {label}{filterCount[key] !== null && filterCount[key] !== undefined ? ` ${filterCount[key].toLocaleString()}` : ''}
                  </button>
                ))}
              </div>
              <input className="srcp-input srcp-search" value={searchText} maxLength={100}
                placeholder="Search part number or description" aria-label="Search parts"
                onChange={(e) => setSearchText(e.target.value)} />
              <span className="srcp-grow" />
              <span className={`srcp-save${save.state === 'error' ? ' srcp-save--err' : ''}`} aria-live="polite">{saveText}</span>
              {save.state === 'error' && (
                <button type="button" className="srcp-btn srcp-btn--sm" onClick={() => flush()}>Try again</button>
              )}
              <div className="srcp-dd" ref={excelRef}>
                <button type="button" className="srcp-btn" aria-haspopup="menu" aria-expanded={excelOpen}
                  disabled={downloading || Boolean(upload && upload.busy)} onClick={() => setExcelOpen((o) => !o)}>
                  {downloading ? 'Preparing…' : (upload && upload.busy ? 'Checking your file…' : 'Work in Excel')}
                </button>
                {excelOpen && (
                  <div className="srcp-ddpanel" role="menu">
                    <button type="button" role="menuitem" className="srcp-dditem" disabled={busyAction} onClick={download}>
                      Download your bid list
                    </button>
                    <button type="button" role="menuitem" className="srcp-dditem" disabled={!canEdit || busyAction || confirming}
                      onClick={() => { setExcelOpen(false); if (fileRef.current) fileRef.current.click(); }}>
                      Upload your filled file
                    </button>
                    <div className="srcp-ddnote">
                      {canEdit ? 'You check every change before it goes into your draft.' : 'Upload is closed while you cannot change your quote.'}
                    </div>
                  </div>
                )}
                <input ref={fileRef} type="file" hidden accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => onFile(e.target.files && e.target.files[0])} />
              </div>
            </div>

            {partsError && <div className="srcp-card-b"><div className="srcp-error" role="alert">{partsError}</div></div>}
            {!view && !partsError && <div className="srcp-card-b srcp-loading">Loading parts…</div>}
            {view && view.items.length === 0 && (
              <div className="srcp-card-b srcp-mut">
                {search ? 'No parts match your search.' : (filter === 'all' ? 'No parts in this event.' : 'No parts in this view.')}
              </div>
            )}
            {view && view.items.length > 0 && (
              <BidGrid
                items={view.items}
                round={round}
                showPrevious={showPrevious}
                rowOf={(pn) => shownRow(pn, draft, edits)}
                needsAnswerOf={(pn) => {
                  const row = shownRow(pn, draft, edits);
                  return rowNeedsAnswer(row) && !isCarriedOnly(pn, row, draft);
                }}
                previousOf={(pn) => previousCell(pn, draft, currency)}
                keepsOf={(pn) => {
                  const c = currentMap[pn];
                  return Boolean(c && isCarried(c, draft) && SUBMITTED_STATES.includes(c.state));
                }}
                serverErrorOf={(pn) => serverErrRef.current[pn]}
                isDirty={(pn) => dirtyRef.current.has(pn)}
                canEdit={gridEditable}
                onChange={onChange}
                onLeave={() => { if (gridEditable) flush(); }}
                onNote={setNoteFor}
              />
            )}

            <div className="srcp-gridfoot">
              {view && (
                <span className="srcp-mut">
                  Showing {view.items.length.toLocaleString()} of {view.total.toLocaleString()} parts.
                  {showPrevious && canEdit ? ` Leave a price empty to keep your round ${round - 1} price.` : ''}
                </span>
              )}
              {invalidCount > 0 && (
                <span className="srcp-bad" role="alert">
                  {invalidCount === 1 ? 'One row needs' : `${invalidCount.toLocaleString()} rows need`} a fix before you can
                  submit: {listText(invalidParts, 5)}. Point at the red cell to see why.
                </span>
              )}
              {invalidCount === 0 && needsAnswer > 0 && (
                <span className="srcp-mut">
                  {needsAnswer === 1 ? 'One part has' : `${needsAnswer.toLocaleString()} parts have`} details but no price or No bid, so {needsAnswer === 1 ? 'it is' : 'they are'} not sent.
                </span>
              )}
              <span className="srcp-grow" />
              {(eventNoteOpen || noteRef.current) ? null : (
                <button type="button" className="srcp-btn srcp-btn--link" onClick={() => setEventNoteOpen(true)}>
                  Add a note to the buyer
                </button>
              )}
            </div>
            {view && view.total_pages > 1 && (
              <div className="srcp-card-b"><Pager data={view} onPage={setPage} label="parts" numbered /></div>
            )}
            {(eventNoteOpen || noteRef.current) && (
              <div className="srcp-card-b" style={{ borderTop: '1px solid var(--ll-hair)' }}>
                <div className="srcp-field">
                  <label className="srcp-label" htmlFor="srcp-event-note">Note to the buyer (sent with your next submission)</label>
                  <textarea id="srcp-event-note" className="srcp-input srcp-textarea" rows={3} maxLength={EVENT_NOTE_MAX}
                    value={noteRef.current} disabled={!gridEditable}
                    onChange={(e) => onNote(e.target.value)} onBlur={() => { if (gridEditable) flush(); }} />
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}
